// src/pages/invoices/components/InvoiceItemsTable.tsx
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { getTaxPreferenceLabel } from '@/lib/constants/common';
import { GstType } from '@/lib/constants/invoice';
import { cn } from '@/lib/utils';
import { formatNumberWithCommas, parseTaxValue } from '@/lib/utils/invoice';
import { InvoiceItem, ItemRef } from '@/types/invoice.types';
import { Item } from '@/types/items.types';
import { DragDropContext, Draggable, Droppable, DropResult } from '@hello-pangea/dnd';
import { FormikErrors, FormikTouched } from 'formik';
import { AlertCircle, GripVertical, Plus, Trash2 } from 'lucide-react';
import ItemDropdown from './ItemDropdown';
import TaxSelect from './TaxSelect';

interface ItemError {
  itemId?: string;
  quantity?: string;
  rate?: string;
  taxValue?: string;
  [key: string]: string | undefined;
}

interface InvoiceItemsTableProps {
  items: InvoiceItem[];
  availableItems: Item[];
  currency: string;
  isCustomerTaxable: boolean;
  onItemsChange: (items: InvoiceItem[]) => void;
  onAddItem: () => void;
  onAddNewItem: (rowId: number) => void;
  // Array-level error (e.g., "At least one item is required")
  error?: string;
  // Item-level errors array
  itemErrors?: FormikErrors<InvoiceItem>[] | string | string[];
  // Touched state for items
  itemsTouched?: FormikTouched<InvoiceItem>[] | boolean;
  taxType: GstType;
}

const InvoiceItemsTable = ({
  items,
  availableItems,
  currency,
  isCustomerTaxable,
  onItemsChange,
  onAddItem,
  onAddNewItem,
  error,
  itemErrors,
  itemsTouched,
  taxType,
}: InvoiceItemsTableProps) => {
  // Get item ID value
  const getItemIdValue = (itemId?: ItemRef | string) => {
    if (!itemId) return undefined;
    return typeof itemId === 'string' ? itemId : itemId._id;
  };

  // Get all errors for an item as a formatted string
  const getItemErrorSummary = (index: number): string[] => {
    if (!itemErrors || typeof itemErrors === 'string') return [];
    if (!Array.isArray(itemErrors)) return [];

    const itemError = itemErrors[index] as ItemError | undefined;
    if (!itemError || typeof itemError === 'string') return [];

    const errors: string[] = [];

    // Check touched state
    const isTouched = itemsTouched && Array.isArray(itemsTouched) ? itemsTouched[index] : true;

    Object.entries(itemError).forEach(([field, message]) => {
      if (message) {
        // Only show if touched
        if (isTouched === true || (typeof isTouched === 'object' && (isTouched as any)[field])) {
          errors.push(message);
        }
      }
    });

    return errors;
  };

  // Collect all item errors for summary display
  const getAllItemErrors = (): { index: number; errors: string[] }[] => {
    if (!itemErrors || typeof itemErrors === 'string') return [];
    if (!Array.isArray(itemErrors)) return [];

    const allErrors: { index: number; errors: string[] }[] = [];

    itemErrors.forEach((itemError, index) => {
      if (itemError && typeof itemError === 'object') {
        const errors = getItemErrorSummary(index);
        if (errors.length > 0) {
          allErrors.push({ index: index + 1, errors });
        }
      }
    });

    return allErrors;
  };

  // Update single item
  const updateItem = (itemId: number, field: string, value: any) => {
    const updatedItems = items.map((item) => {
      if (item.id !== itemId) return item;

      const next = { ...item, [field]: value };
      next.quantity = Number(next.quantity || 1);
      next.rate = Number(next.rate || 0);

      // If item was selected from dropdown
      if (field === 'itemId') {
        const selectedItem = availableItems.find((ai) => ai._id === value);
        if (selectedItem) {
          next.itemName = selectedItem.itemName;
          next.description = selectedItem.description || '';
          next.rate = Number(selectedItem.sellingPrice || 0);
          next.hsnSac =
            selectedItem.itemType === 'service' ? selectedItem.sacCode : selectedItem.hsnCode;
          next.taxPreference = selectedItem.taxPreference || '';
          next.sellingPrice = selectedItem.sellingPrice || 0;
          next.itemType = selectedItem.itemType || 'goods';
        }
      }

      // Tax handling
      if (field === 'taxValue') {
        next.taxValue = value;
        next.taxRate = isCustomerTaxable ? parseTaxValue(value, taxType).rate : 0;
      }

      // Calculate amounts
      const baseAmount = next.rate * next.quantity;
      const taxAmount = (baseAmount * (next.taxRate || 0)) / 100;
      next.taxAmount = Number(taxAmount.toFixed(2));
      next.amount = Number((baseAmount + taxAmount).toFixed(2));
      next.baseAmount = Number(baseAmount.toFixed(2));

      return next;
    });

    onItemsChange(updatedItems);
  };

  // Remove item
  const removeItem = (itemId: number) => {
    onItemsChange(items.filter((item) => item.id !== itemId));
  };

  // Handle drag end
  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const updated = Array.from(items);
    const [moved] = updated.splice(result.source.index, 1);
    updated.splice(result.destination.index, 0, moved);
    onItemsChange(updated);
  };

  // Column definitions
  const columns = [
    { key: 'drag', header: '', className: 'w-8' },
    { key: 'item', header: 'Item', className: 'min-w-[200px]' },
    { key: 'code', header: 'Code', className: 'w-24' },
    { key: 'qty', header: 'Qty', className: 'w-20' },
    { key: 'rate', header: 'Rate', className: 'w-24' },
    { key: 'tax', header: 'Tax', className: 'min-w-[190px]' },
    { key: 'amount', header: 'Amount', className: 'w-28' },
    { key: 'actions', header: 'Actions', className: 'w-16 text-right sticky right-0 bg-muted' },
  ];

  const allItemErrors = getAllItemErrors();

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">Invoice Items</h3>
        <Button type="button" variant="outline" size="sm" onClick={onAddItem}>
          <Plus className="h-4 w-4 mr-1" />
          Add Item
        </Button>
      </div>

      {/* Table */}
      <div className="rounded-md border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full caption-bottom text-sm">
            {/* Table Header */}
            <thead className="[&_tr]:border-b bg-muted">
              <tr className="border-b transition-colors">
                {columns.map((col) => (
                  <th
                    key={col.key}
                    className={cn(
                      'h-12 px-4 text-left align-middle font-medium text-muted-foreground',
                      col.className
                    )}
                  >
                    {col.header}
                  </th>
                ))}
              </tr>
            </thead>

            {/* Table Body with Drag & Drop */}
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="invoice-items">
                {(provided) => (
                  <tbody
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className="[&_tr:last-child]:border-0"
                  >
                    {items.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="h-24 text-center">
                          <div className="flex flex-col items-center justify-center text-muted-foreground">
                            <p>No items added yet.</p>
                            <Button
                              type="button"
                              variant="link"
                              size="sm"
                              onClick={onAddItem}
                              className="mt-2"
                            >
                              <Plus className="h-4 w-4 mr-1" />
                              Add your first item
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      items.map((item, index) => {
                        return (
                          <Draggable
                            key={item.id.toString()}
                            draggableId={item.id.toString()}
                            index={index}
                          >
                            {(draggable, snapshot) => (
                              <tr
                                ref={draggable.innerRef}
                                {...draggable.draggableProps}
                                className={cn(
                                  'group border-b transition-colors',
                                  snapshot.isDragging ? 'bg-muted shadow-lg' : 'hover:bg-muted'
                                )}
                              >
                                {/* Drag Handle */}
                                <td
                                  {...draggable.dragHandleProps}
                                  className="px-4 py-3 align-middle cursor-move"
                                >
                                  <GripVertical className="h-4 w-4 text-muted-foreground" />
                                </td>

                                {/* Item Dropdown */}
                                <td className="px-4 py-3 align-middle">
                                  <div className="space-y-1">
                                    <ItemDropdown
                                      items={availableItems}
                                      value={getItemIdValue(item.itemId)}
                                      onChange={(itemId) => updateItem(item.id, 'itemId', itemId)}
                                      onAddNew={() => onAddNewItem(item.id)}
                                    />
                                    {item.description && (
                                      <p className="text-xs text-muted-foreground">
                                        {item.description}
                                      </p>
                                    )}
                                    {item.taxPreference && (
                                      <p className="text-xs text-primary">
                                        {getTaxPreferenceLabel(item.taxPreference)}
                                      </p>
                                    )}
                                  </div>
                                </td>

                                {/* HSN/SAC Code */}
                                <td className="px-4 py-3 align-middle">
                                  <span className="font-mono text-sm text-muted-foreground">
                                    {item.hsnSac || '-'}
                                  </span>
                                </td>

                                {/* Quantity */}
                                <td className="px-4 py-3 align-middle">
                                  <Input
                                    type="number"
                                    min="1"
                                    value={item.quantity}
                                    onChange={(e) => {
                                      const qty = parseInt(e.target.value, 10) || 1;
                                      updateItem(item.id, 'quantity', qty);
                                    }}
                                    className="w-16 h-9 bg-background"
                                  />
                                </td>

                                {/* Rate */}
                                <td className="px-4 py-3 align-middle">
                                  <span className="font-medium">
                                    {/* {currencySymbols[currency] || ''} */}
                                    {formatNumberWithCommas(Number(item.rate || 0))}
                                  </span>
                                </td>

                                {/* Tax */}
                                <td className="px-4 py-3 align-middle">
                                  <div className="space-y-1">
                                    <TaxSelect
                                      value={item.taxValue || ''}
                                      onChange={(val) => updateItem(item.id, 'taxValue', val)}
                                      isCustomerTaxable={isCustomerTaxable}
                                      taxType={taxType}
                                      error={allItemErrors
                                        .find((e) => e.index === index + 1)
                                        ?.errors.some((msg) => msg.includes('Tax'))}
                                    />
                                    {allItemErrors
                                      .find((e) => e.index === index + 1)
                                      ?.errors.some((msg) => msg.includes('Tax')) && (
                                      <p className="text-[10px] text-destructive">
                                        Tax is required
                                      </p>
                                    )}
                                  </div>
                                </td>

                                {/* Amount */}
                                <td className="px-4 py-3 align-middle">
                                  <span className="font-semibold">
                                    {/* {currencySymbols[currency] || ''} */}
                                    {formatNumberWithCommas(
                                      Number(item.amount || item.rate * item.quantity)
                                    )}
                                  </span>
                                </td>

                                {/* Delete */}
                                <td
                                  className={cn(
                                    'px-4 py-3 align-middle text-right sticky right-0 transition-colors bg-muted',
                                    snapshot.isDragging
                                      ? 'bg-muted'
                                      : 'bg-background group-hover:bg-muted'
                                  )}
                                >
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-destructive hover:bg-destructive/10 hover:text-destructive"
                                    onClick={() => removeItem(item.id)}
                                  >
                                    <Trash2 className="!h-[18px] !w-[18px]" />
                                  </Button>
                                </td>
                              </tr>
                            )}
                          </Draggable>
                        );
                      })
                    )}
                    {provided.placeholder}
                  </tbody>
                )}
              </Droppable>
            </DragDropContext>
          </table>
        </div>
      </div>

      {/* Error Messages Section */}
      {(error || allItemErrors.length > 0) && (
        <div className="rounded-md bg-destructive/10 border border-destructive/20 p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-destructive mt-0.5 flex-shrink-0" />
            <div className="space-y-2">
              {/* Array-level error */}
              {error && <p className="text-sm font-medium text-destructive">{error}</p>}

              {/* Individual item errors */}
              {allItemErrors.length > 0 && (
                <div className="space-y-1">
                  {!error && (
                    <p className="text-sm font-medium text-destructive">
                      Please fix the following errors:
                    </p>
                  )}
                  <ul className="list-disc list-inside space-y-1">
                    {allItemErrors.map(({ index, errors }) => (
                      <li key={index} className="text-sm text-destructive">
                        <span className="font-medium">Item #{index}:</span> {errors.join(', ')}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceItemsTable;
